<script setup lang="ts">
  import { LazyCoreConfirmationModal } from '#components';
  import { stores } from '~/store';
  import { useFriendsStore } from '~/store/friends';

  const EUserStatus = {
    ONLINE: 'online',
    OFFLINE: 'offline',
    BLOCKED: 'blocked',
  } as const;

  const FRIENDS_STATUSES = [
    { label: 'ONLINE', slot: EUserStatus.ONLINE, defaultOpen: true },
    { label: 'OFFLINE', slot: EUserStatus.OFFLINE },
    { label: 'BLOCKED', slot: EUserStatus.BLOCKED },
  ];

  const openedStatus = ref([]);

  const friendsStore = useFriendsStore();

  const friendList = computed(() => friendsStore.acceptedFriends || []);
  const userFriendsOnline = computed(() => friendList.value.filter((friend: IFriendData) => friend.online));
  const userFriendsOffline = computed(() => friendList.value.filter((friend: IFriendData) => !friend.online));
  const blockedUsers = computed(() => friendsStore.blockedFriends || []);

  const userCounters = computed(() => ({
    [EUserStatus.ONLINE]: userFriendsOnline.value.length,
    [EUserStatus.OFFLINE]: userFriendsOffline.value.length,
    [EUserStatus.BLOCKED]: blockedUsers.value.length,
  }));

  async function unblockFriend(friend: IFriendData) {
    const isConfirmed = await stores.modals.open('unblock-friend', LazyCoreConfirmationModal, {
      title: 'Unblock Friend',
      text: `Are you sure you want to unblock ${friend.displayName}?`,
    });

    if (!isConfirmed) return;

    await friendsStore.unblockFriend(friend);
  }

  async function blockUser(friend: IFriendData) {
    const isConfirmed = await stores.modals.open('block-friend', LazyCoreConfirmationModal, {
      title: 'Block Friend',
      text: `Are you sure you want to block ${friend.displayName}?`,
    });

    if (!isConfirmed) return;

    await friendsStore.blockFriend(friend.friendId);
  }

  async function removeFriend(friend: IFriendData) {
    const isConfirmed = await stores.modals.open('remove-friend', LazyCoreConfirmationModal, {
      title: 'Removing Friend',
      text: `Are you sure you want to remove ${friend.displayName} from friends?`,
    });

    if (!isConfirmed) return;

    await useFriendsStore().removeFriend(friend.friendId);
  }
</script>

<template>
  <div
    class="flex flex-col px-0 pt-0 w-full max-w-[400px] bg-[hsla(0,0%,100%,0.07)] rounded-xl max-h-[400px] md:max-h-none h-full overflow-y-hidden"
  >
    <GameFriendsListCardHeader title="FRIENDS LIST">
      <template #icon>
        <SvgoFriendMenu class="size-10 fill-white" />
      </template>
    </GameFriendsListCardHeader>
    <UAccordion
      v-model="openedStatus"
      class="px-3"
      :ui="{ label: 'text-white grow shrink-0 basis-auto flex items-center flex-wrap', trailingIcon: 'text-white' }"
      :items="FRIENDS_STATUSES"
      multiple
    >
      <template #default="{ item }">
        <div
          class="w-full gap-y-2 flex justify-between flex-wrap flex-col xs:flex-row xs:items-center flex-shrink-0 flex-grow-1"
        >
          <div class="flex justify-between items-center gap-2 w-full">
            <h2
              class="text-start break-words font-inter font-medium text-[18px] md:text-[20px] leading-[150%] grow shrink-0 basis-auto flex"
            >
              {{ item.label }}
            </h2>
            <div class="flex items-center gap-2">
              <div class="flex items-center justify-between w-full px-2">
                <div class="flex items-center gap-1">
                  <UIcon name="i-heroicons-user" />
                  <span>{{ userCounters[item.slot] }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #online>
        <ul class="pr-0 sm:pr-4 space-y-2 max-h-[390px] overflow-auto py-4">
          <li
            v-if="!userFriendsOnline.length"
            class="text-white text-center font-size-20 font-bold py-3 bg-white/5 rounded-xl"
          >
            Empty
          </li>
          <li
            v-for="(child, index) in userFriendsOnline"
            :key="index"
            class="flex items-center px-3 py-2 bg-white/5 rounded-lg transition-all"
          >
            <span class="text-sm sm:text-base md:text-lg font-bold text-white">
              {{ child.displayName }}
            </span>
            <div class="ml-auto flex gap-2">
              <UTooltip text="Block User">
                <UButton icon="i-heroicons-no-symbol" size="xs" class="text-white" @click="blockUser(child)" />
              </UTooltip>
              <UTooltip text="Remove Friend">
                <UButton icon="i-heroicons-minus" size="xs" class="text-white" @click="removeFriend(child)" />
              </UTooltip>
            </div>
          </li>
        </ul>
      </template>

      <template #offline>
        <ul class="pr-0 sm:pr-4 space-y-2 max-h-[390px] overflow-auto py-4">
          <li
            v-if="!userFriendsOffline.length"
            class="text-white text-center font-size-20 font-bold py-3 bg-white/5 rounded-xl"
          >
            Empty
          </li>
          <li
            v-for="(child, index) in userFriendsOffline"
            :key="index"
            class="flex items-center px-3 py-2 rounded-lg bg-white/5 transition-all"
          >
            <span class="text-sm sm:text-base md:text-lg font-bold text-white">
              {{ child.displayName }}
            </span>
            <div class="ml-auto flex gap-2">
              <UTooltip text="Block User">
                <UButton icon="i-heroicons-no-symbol" size="xs" class="text-white" @click="blockUser(child)" />
              </UTooltip>
              <UTooltip text="Remove Friend">
                <UButton icon="i-heroicons-minus" size="xs" class="text-white" @click="removeFriend(child)" />
              </UTooltip>
            </div>
          </li>
        </ul>
      </template>

      <template #blocked>
        <ul class="pr-0 sm:pr-4 space-y-2 max-h-[390px] overflow-auto py-4">
          <li
            v-if="!blockedUsers.length"
            class="text-white text-center font-size-20 font-bold py-3 bg-white/5 rounded-xl"
          >
            Empty
          </li>
          <li
            v-for="(child, index) in blockedUsers"
            :key="index"
            class="flex items-center px-4 py-2 bg-white/5 rounded-lg"
          >
            <span class="text-sm sm:text-base md:text-lg font-bold text-white">
              {{ child.displayName }}
            </span>
            <div class="ml-auto">
              <UTooltip text="Unblock User">
                <UButton icon="i-heroicons-no-symbol" color="green" size="xs" @click="unblockFriend(child)" />
              </UTooltip>
            </div>
          </li>
        </ul>
      </template>
    </UAccordion>
  </div>
</template>
